import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  Home,
  FolderOpen,
  Pill,
  MessagesSquare,
  Package,
  Settings,
  Phone,
  LogOut,
  Wifi,
  BatteryFull,
} from "lucide-react";
import { useSanitarioAuth } from "../context/SanitarioAuthContext";
import Oss118Dialog from "./oss/Oss118Dialog";
import SanitarioOssBridge from "./SanitarioOssBridge";

const NAV = [
  { to: "/sanitario", end: true, label: "Home Sanitaria", icon: Home },
  { to: "/sanitario/scheda", label: "Scheda Ospite", icon: FolderOpen },
  { to: "/sanitario/terapie", label: "Terapie e Farmaci", icon: Pill },
  { to: "/sanitario/comunicazioni", label: "Comunicazioni", icon: MessagesSquare },
  { to: "/sanitario/deblistering", label: "Deblistering", icon: Package },
  { to: "/sanitario/impostazioni", label: "Impostazioni", icon: Settings },
];

function getInitials(op) {
  return `${op?.nome?.[0] ?? ""}${op?.cognome?.[0] ?? ""}`.toUpperCase() || "?";
}

function formatHeaderDate(d) {
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function SanitarioLayoutShell() {
  const { activeOperatore, loading, logoutActive } = useSanitarioAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [open118, setOpen118] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-slate-100 flex items-center justify-center"
        style={{ minHeight: "100dvh" }}
      >
        <div className="w-10 h-10 border-2 border-[#C83048] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activeOperatore) {
    return <Navigate to="/sanitario-login" replace />;
  }

  return (
    <SanitarioOssBridge>
      <div className="flex h-dvh overflow-hidden bg-slate-100 text-slate-800 print:block print:h-auto print:overflow-visible print:bg-white">
        {/* Sidebar */}
        <aside className="hidden md:flex w-[240px] shrink-0 min-h-0 flex-col overflow-hidden bg-[#901030] text-white print:hidden">
          <div className="px-5 pt-6 pb-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/images/logo-sanita-mark.png"
                alt=""
                className="h-9 w-9 rounded-[10px] object-contain shrink-0"
              />
              <div className="leading-tight">
                <p className="text-[11px] font-semibold tracking-[0.12em] text-white/70 uppercase">
                  Affidya
                </p>
                <p className="text-sm font-bold">Sanità Rapida</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
            {NAV.map(({ to, end, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition touch-manipulation ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-white/75 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4 shrink-0 opacity-90" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="px-3 pb-3 pt-2 space-y-3">
            <button
              type="button"
              onClick={() => setOpen118(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-red-400/90 bg-red-500/15 px-3 py-3 text-sm font-bold text-red-100 hover:bg-red-500/25 transition touch-manipulation"
            >
              <Phone className="w-4 h-4" />
              Pulsante 118 EMERGENZA
            </button>

            <div className="rounded-xl bg-white/10 px-3 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-slate-300 text-[#901030] flex items-center justify-center text-sm font-bold shrink-0">
                  {getInitials(activeOperatore)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">
                    {activeOperatore.nome} {activeOperatore.cognome}
                  </p>
                  <p className="text-xs text-white/60 truncate">
                    {activeOperatore.ruolo_label || "Operatore sanitario"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={logoutActive}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/15 px-2 py-2 text-xs font-medium touch-manipulation"
              >
                <LogOut className="w-3.5 h-3.5" />
                Esci
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <header className="shrink-0 z-40 bg-white border-b border-slate-200 px-4 md:px-6 py-3 print:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm md:text-base font-semibold text-slate-800 truncate">
                  Benvenuto, {activeOperatore.nome} {activeOperatore.cognome}{" "}
                  <span className="font-normal text-slate-500">
                    – Modulo Sanitario Rapido
                  </span>
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500 shrink-0">
                <span className="capitalize">{formatHeaderDate(now)}</span>
                <span className="font-semibold text-slate-700 tabular-nums">
                  {now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </span>
                <Wifi className="w-4 h-4 text-slate-400" />
                <span className="inline-flex items-center gap-1">
                  <BatteryFull className="w-4 h-4 text-emerald-500" />
                </span>
              </div>
              {/* Mobile nav shortcut */}
              <div className="flex md:hidden items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOpen118(true)}
                  className="rounded-lg bg-red-600 text-white text-xs font-bold px-2.5 py-2 touch-manipulation"
                >
                  118
                </button>
                <Link
                  to="/sanitario"
                  className="rounded-lg bg-[#C83048] text-white text-xs font-semibold px-2.5 py-2"
                >
                  Home
                </Link>
              </div>
            </div>
          </header>

          {/* Mobile bottom nav */}
          <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#901030] text-white border-t border-white/10 flex justify-around py-2 px-1 print:hidden safe-area-pb">
            {[
              { to: "/sanitario", label: "Home", icon: Home, end: true },
              { to: "/sanitario/scheda", label: "Scheda", icon: FolderOpen },
              { to: "/sanitario/terapie", label: "Terapie", icon: Pill },
              { to: "/sanitario/comunicazioni", label: "Msg", icon: MessagesSquare },
              { to: "/sanitario/impostazioni", label: "Altro", icon: Settings },
            ].map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                    isActive ? "text-white" : "text-white/60"
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            ))}
          </nav>

          <main className="flex-1 min-h-0 overflow-auto px-4 md:px-6 py-4 md:py-5 pb-20 md:pb-6">
            <Outlet context={{ open118: () => setOpen118(true), navigate }} />
            <p className="mt-6 mb-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-400 print:hidden">
              <span className="inline-block w-3 h-3 rounded-sm border border-slate-300" />
              Accesso protetto – Modulo Sanitario Rapido di Affidya
            </p>
          </main>
        </div>

        <Oss118Dialog open={open118} onClose={() => setOpen118(false)} />
      </div>
    </SanitarioOssBridge>
  );
}

export default function SanitarioLayout() {
  return <SanitarioLayoutShell />;
}
